import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

const PAGE_SIZES = [10, 20, 50, 100];

export function TablePagination({
  data = [],
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  className = '',
}) {
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const hasItems = totalItems > 0;
  const showing = hasItems ? `${startIndex + 1}-${endIndex} of ${totalItems}` : '0 of 0';

  const handlePrev = () => onPageChange?.(Math.max(1, currentPage - 1));
  const handleNext = () => onPageChange?.(Math.min(totalPages, currentPage + 1));

  if (!hasItems && totalItems === 0) return null;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-4 border-t border-slate-200 ${className}`}>
      <div className="flex items-center gap-4 text-sm text-slate-600">
        <span>Showing {showing}</span>
        {totalItems > PAGE_SIZES[0] && (
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => { onPageSizeChange?.(Number(v)); onPageChange?.(1); }}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                disabled={currentPage <= 1}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
            </PaginationItem>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 5) p = i + 1;
              else if (currentPage <= 3) p = i + 1;
              else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
              else p = currentPage - 2 + i;
              return (
                <PaginationItem key={p}>
                  <Button
                    variant={p === currentPage ? 'outline' : 'ghost'}
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => onPageChange?.(p)}
                  >
                    {p}
                  </Button>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                className="gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

